import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";

export const EditorContainer = styled(Box)(({ theme, width, height }) => ({
  display: "flex",
  width: width || "100%",
  height: height || "400px",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  fontFamily: 'Consolas, Monaco, "Andale Mono", monospace',
  overflow: "hidden",
  backgroundColor: theme.palette.background.paper,
}));

export const LineNumbers = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  backgroundColor:
    theme.palette.mode === "light"
      ? theme.palette.grey[100]
      : theme.palette.grey[900],
  borderRight: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  userSelect: "none",
  textAlign: "right",
  minWidth: "40px",
  overflowY: "auto",
  fontSize: "0.875rem",
}));

export const LineNumber = styled(Box)({
  lineHeight: "1.5rem",
  fontSize: "0.875rem",
});

export const EditorWrapper = styled(Box)({
  flex: 1,
  position: "relative",
  overflow: "hidden",
});

export const CodeEditor = styled(Box)({
  width: "100%",
  height: "100%",
  overflow: "auto",
  position: "relative",
  display: "flex",
  flexDirection: "column",
});

export const Pre = styled("pre")(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
  fontFamily: "inherit",
  fontSize: "0.875rem",
  lineHeight: "1.5rem",
  backgroundColor: "transparent",
  whiteSpace: "pre",
  tabSize: 2,
  color: "inherit",
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
  pointerEvents: "none", // Prevent interaction with the Pre layer
}));

export const Code = styled("code")(({ theme }) => ({
  fontFamily: "inherit",
  fontSize: "0.875rem",
  display: "block",
  color: theme.palette.text.primary,
  whiteSpace: "pre-wrap",
  wordWrap: "break-word",
  "& .token": {
    transition: "none",
    fontWeight: "500",
  },
  // Comments
  "& .token.comment": {
    color: theme.palette.mode === "dark" ? "#6A9955" : "#008000",
    fontStyle: "italic",
  },
  // Strings - used in JSON for both keys and string values
  "& .token.string": {
    color: theme.palette.mode === "dark" ? "#CE9178" : "#A31515",
    fontWeight: "400",
  },
  // Numbers
  "& .token.number": {
    color: theme.palette.mode === "dark" ? "#B5CEA8" : "#098658",
    fontWeight: "600",
  },
  // JSON specific - property keys
  "& .token.property": {
    color: theme.palette.mode === "dark" ? "#9CDCFE" : "#36A9F4",
    fontWeight: "500",
  },
  // Keywords like true, false, null
  "& .token.keyword, & .token.boolean, & .token.null": {
    color: theme.palette.mode === "dark" ? "#569CD6" : "#0000FF",
    fontWeight: "600",
  },
  // Operators and punctuation
  "& .token.operator, & .token.punctuation": {
    color: theme.palette.mode === "dark" ? "#D4D4D4" : "#000000",
    fontWeight: "600",
  },
  // XML/HTML specific
  "& .token.tag": {
    color: theme.palette.mode === "dark" ? "#569CD6" : "#800000",
    fontWeight: "600",
  },
  "& .token.attr-name": {
    color: theme.palette.mode === "dark" ? "#9CDCFE" : "#E50000",
    fontWeight: "500",
  },
  "& .token.attr-value": {
    color: theme.palette.mode === "dark" ? "#CE9178" : "#0000FF",
    fontWeight: "400",
  },
}));

export const TextArea = styled("textarea")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  padding: theme.spacing(1),
  border: "none",
  outline: "none",
  resize: "none",
  lineHeight: "1.5rem",
  fontSize: "0.875rem",
  fontFamily: "inherit",
  backgroundColor: "transparent",
  color: "transparent",
  whiteSpace: "pre-wrap",
  wordWrap: "break-word",
  overflow: "hidden",
  caretColor: theme.palette.mode === "dark" ? "#ffffff" : "#000000", // Keep the caret visible
  zIndex: 2, // Ensure it is above the Pre layer
  "&::selection": {
    backgroundColor: `${theme.palette.primary.light} !important`,
    color: "transparent", // Keep selection visible but text transparent
  },
}));
